import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MultiSelect } from "./MultiSelect";
import { AccountLookup } from "./AccountLookup";
import { isPhoneKey } from "../lib/utils-format";

export default function EntityForm({ open, onOpenChange, fields, initial, onSubmit, title, saving, dynamicOptions = {}, headerSlot = null, patch = null }) {
  const [form, setForm] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setForm(initial || {}); setError(""); }
  }, [open, initial]);

  // Allow a parent (e.g. Angela) to merge generated values into the form.
  useEffect(() => {
    if (patch && patch.seq) {
      const { seq, ...values } = patch;
      setForm((f) => ({ ...f, ...values }));
    }
  }, [patch?.seq]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (error) setError(""); };

  // Digits helper — strips our literal "+1" prefix and any formatting to a bare 10-digit US number
  const phoneDigits = (val) => {
    let s = String(val ?? "").trim();
    if (s.startsWith("+1")) s = s.slice(2);
    let d = s.replace(/\D/g, "");
    if (d.length === 11 && d.startsWith("1")) d = d.slice(1); // legacy 1XXXXXXXXXX (no +)
    return d.slice(0, 10);
  };

  // Progressive display: +1 (XXX) XXX-XXXX
  const phoneDisplay = (val) => {
    const d = phoneDigits(val);
    if (!d) return "";
    if (d.length <= 3) return `+1 (${d}`;
    if (d.length <= 6) return `+1 (${d.slice(0, 3)}) ${d.slice(3)}`;
    return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const submit = (e) => {
    e.preventDefault();
    for (const f of fields) {
      if (isPhoneKey(f.key)) {
        const raw = form[f.key];
        const hasValue = raw !== undefined && raw !== null && String(raw).trim() !== "";
        if ((f.required || hasValue) && phoneDigits(raw).length !== 10) {
          setError(`${f.label} must be a valid US number in +1 (XXX) XXX-XXXX format`);
          return;
        }
      }
    }
    setError("");
    onSubmit(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto slim-scroll">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{title}</DialogTitle>
        </DialogHeader>
        {headerSlot}
        <form onSubmit={submit} className="space-y-4 py-2">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={`field-${f.key}`} className="text-xs uppercase tracking-wide text-muted-foreground">
                {f.label}{f.required && " *"}
              </Label>
              {f.type === "textarea" ? (
                <Textarea id={`field-${f.key}`} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`form-${f.key}`} />
              ) : f.type === "account-lookup" ? (
                <AccountLookup
                  value={form[f.key] || ""}
                  onSelect={(name, accountId) => setForm((prev) => ({ ...prev, [f.key]: name, [f.lookupIdKey || "account_id"]: accountId }))}
                  testid={`form-${f.key}`}
                />
              ) : f.type === "multiselect" ? (
                <MultiSelect options={f.options} value={form[f.key] || []} onChange={(v) => set(f.key, v)} testid={`form-${f.key}`} />
              ) : f.type === "select" ? (
                (() => {
                  const raw = f.dynamic ? (dynamicOptions[f.key] || []) : (f.options || []);
                  const opts = raw.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
                  return (
                    <Select value={form[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                      <SelectTrigger id={`field-${f.key}`} data-testid={`form-${f.key}`}><SelectValue placeholder={f.dynamic && opts.length === 0 ? "None available" : "Select…"} /></SelectTrigger>
                      <SelectContent>
                        {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  );
                })()
              ) : isPhoneKey(f.key) ? (
                <Input
                  id={`field-${f.key}`}
                  type="tel"
                  value={phoneDisplay(form[f.key] ?? "")}
                  onChange={(e) => { const d = phoneDigits(e.target.value); set(f.key, d ? `+1${d}` : ""); }}
                  placeholder="+1 (XXX) XXX-XXXX"
                  required={f.required}
                  data-testid={`form-${f.key}`}
                />
              ) : (
                <Input
                  id={`field-${f.key}`}
                  type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                  value={form[f.key] ?? ""}
                  onChange={(e) => set(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)}
                  required={f.required}
                  data-testid={`form-${f.key}`}
                />
              )}
            </div>
          ))}
          <DialogFooter>
            {error && <p className="text-xs text-destructive mr-auto self-center" data-testid="form-error">{error}</p>}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="form-submit-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
